/**
 * HTTP Security Utilities
 * Provides defense-in-depth measures against CRLF injection and SSRF attacks
 */

/**
 * Sanitize HTTP header values to prevent CRLF injection
 * Removes newline characters (\r, \n) that could be used for header injection attacks
 * @param {string} value - The header value to sanitize
 * @returns {string} - Sanitized header value
 */
export const sanitizeHeaderValue = (value) => {
  if (typeof value !== "string") return value;
  return value.replace(/[\r\n]/g, "");
};

/**
 * Sanitize all headers in an object
 * @param {Object} headers - Object containing header key-value pairs
 * @returns {Object} - Sanitized headers object
 */
export const sanitizeHeaders = (headers) => {
  if (!headers || typeof headers !== "object") return headers;

  const sanitized = {};
  for (const [key, value] of Object.entries(headers)) {
    sanitized[sanitizeHeaderValue(key)] = sanitizeHeaderValue(value);
  }
  return sanitized;
};

/**
 * Validate URL to prevent SSRF attacks
 * Ensures URLs use safe protocols and don't target internal resources
 * @param {string} url - The URL to validate
 * @param {Object} options - Validation options
 * @param {string[]} options.allowedProtocols - Allowed URL protocols (default: ['http:', 'https:'])
 * @param {boolean} options.allowLocalhost - Allow localhost URLs (default: false in production)
 * @param {boolean} options.allowPrivateIPs - Allow private IP ranges (default: false)
 * @returns {boolean} - True if URL is safe, false otherwise
 */
export const isUrlSafe = (
  url,
  {
    allowedProtocols = ["http:", "https:"],
    allowLocalhost = process.env.NODE_ENV === "development",
    allowPrivateIPs = false,
  } = {}
) => {
  try {
    const parsedUrl = new URL(url);

    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      return false;
    }

    const hostname = parsedUrl.hostname.toLowerCase();

    if (!allowLocalhost) {
      if (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "::1" ||
        hostname.endsWith(".local")
      ) {
        return false;
      }
    }

    if (!allowPrivateIPs) {
      const ipv4PrivateRanges = [
        /^10\./,
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
        /^192\.168\./,
        /^169\.254\./,
      ];

      if (ipv4PrivateRanges.some((pattern) => pattern.test(hostname))) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Validate and sanitize URL
 * @param {string} url - The URL to validate and sanitize
 * @param {Object} options - Validation options (same as isUrlSafe)
 * @throws {Error} - If URL is not safe
 * @returns {string} - The validated URL
 */
export const validateUrl = (url, options = {}) => {
  if (!isUrlSafe(url, options)) {
    throw new Error("Invalid or unsafe URL");
  }
  return url;
};
